import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import Select, { components } from 'react-select';

import WpToastManager from 'Wpds/components/toast/WpToastManager';
import WpButton from 'Wpds/components/button/WpButton';
import WpIcon from 'Wpds/components/icon/WpIcon';
import WpButtonGroup from 'Wpds/components/button/WpButtonGroup';
import StyledCheckbox from 'Shared/components/Checkboxes/StyledCheckbox/StyledCheckbox';
import { getWorkspaceDisplayLabel } from 'Shared/selectors/workspace';
import { duplicateTodo } from 'Shared/api';
import QuestionMark from 'Shared/components/QuestionMark/QuestionMark';
import { WpModal, WpModalHeader, WpModalBody, WpModalFooter } from 'Wpds/components/modal';
import { useToggle } from 'Shared/utils/hooks';

import './DuplicateModal.scss';

// Hooks
import { useApp } from 'Shared/utils/hooks/use-app';
import { useWorkspace } from 'Shared/utils/hooks/use-workspace';

const selectStyles = {
  menu: styles => ({ ...styles, zIndex: 3 }),
  multiValue: styles => ({ ...styles, backgroundColor: '#ABC3FD', borderRadius: '4px' }),
  multiValueLabel: base => ({
    ...base,
    color: '#0238BB',
  }),
  multiValueRemove: base => ({
    ...base,
    color: '#0238BB',
    '&:hover': {
      backgroundColor: '#ABC3FD',
      color: '#285BD6',
      cursor: 'pointer',
    },
  }),
  clearIndicator: base => ({
    ...base,
    color: '#B6BAC4',
    '&:hover': {
      cursor: 'pointer',
    },
  }),
  menuPortal: base => ({ ...base, zIndex: '1050' }),
};

const allTeammatesValue = 'all_teammates';

type Props = {
  itemUuids: Array<string>;
  isOpen: boolean;
  onToggle: () => void;
};

const DuplicateModal = ({ itemUuids, isOpen, onToggle }: Props) => {
  const dispatch = useDispatch();
  const [isLoading, toggleIsLoading] = useToggle(false);
  const [duplicateWithWorkspaces, setDuplicateWithWorkspaces] = useState([]);
  const [isMakeCopy, setIsMakeCopy] = useState(false);

  const workspaces = useSelector(state =>
    state.workspaces.map(workspace => ({ ...workspace, displayName: getWorkspaceDisplayLabel(workspace.id)(state) }))
  );

  const {
    app: { clientSyncToken },
  } = useApp();

  const { id: workspaceId } = useWorkspace();
  const optionsForSelect = workspaces
    .filter(workspace => workspace.id !== workspaceId)
    .map(({ id, displayName }) => ({ value: id, label: displayName }));

  const makeCopyToggle = () => {
    setIsMakeCopy(!isMakeCopy);
    if (isMakeCopy) setDuplicateWithWorkspaces([]);
  };

  const handleSelectChange = values => {
    if (!Array.isArray(values)) return setDuplicateWithWorkspaces(values ? [values] : []);
    if ((values || []).find(({ value }) => value === allTeammatesValue))
      setDuplicateWithWorkspaces(
        workspaces
          .filter(({ id, isPersonal, isGroup }) => id !== workspaceId && !isPersonal && !isGroup)
          .map(({ id, displayName }) => ({ value: id, label: displayName }))
      );
    else setDuplicateWithWorkspaces(values || []);
    return null;
  };

  const handleDuplicateClick = async () => {
    const workspacesIds = duplicateWithWorkspaces.map(item => item.value);

    if (!workspacesIds.length) {
      WpToastManager.info('Please select at least one workspace!');
      return;
    }

    toggleIsLoading();

    try {
      const isMoveItems = !isMakeCopy;
      const response = await duplicateTodo({
        itemUuids,
        duplicateFromWorkspaceId: workspaceId,
        duplicateToWorkspaceIds: workspacesIds,
        isMoveItems,
        clientSyncToken,
      });
      if (response.status === 200) {
        WpToastManager.success('Success');
        dispatch({
          type: 'DUPLICATE_ACROSS_WORKSPACES_RESPONSE',
          payload: { data: response.data.duplicatedItems, itemUuids, isMoveItems },
        });
        toggleIsLoading();
        onToggle();
      }
    } catch (e) {
      WpToastManager.danger('Something went wrong! Please try again');
      toggleIsLoading();
    }
  };

  return (
    <WpModal size="sm" isOpen={isOpen} onToggle={onToggle}>
      <WpModalHeader>Move To Another Workspace</WpModalHeader>
      <WpModalBody>
        <div className="DuplicateModal">
          <div className="DuplicateModal__title mb-2">Choose the workspace you would like to move this item to.</div>
          <div className="DuplicateModal__label">Workspaces</div>
          <Select
            isSearchable
            isMulti={isMakeCopy}
            className="DuplicateModal__select"
            menuPortalTarget={document.body}
            components={{
              MultiValueRemove: props => (
                <components.MultiValueRemove {...props}>
                  <span className="invite-collaborators-input-remove">
                    <WpIcon name="times-circle" />
                  </span>
                </components.MultiValueRemove>
              ),
              ClearIndicator: props => (
                <components.ClearIndicator {...props}>
                  <WpIcon name="times" />
                </components.ClearIndicator>
              ),
              DropdownIndicator: isMakeCopy
                ? () => (
                    <span className="DuplicateModal__search-icon">
                      <WpIcon name="search" />
                    </span>
                  )
                : () => (
                    <span className="pr-2">
                      <WpIcon name="chevron-down" />
                    </span>
                  ),
              IndicatorSeparator: null,
            }}
            options={
              isMakeCopy
                ? [{ value: allTeammatesValue, label: 'All teammates' }, ...optionsForSelect]
                : optionsForSelect
            }
            styles={selectStyles}
            placeholder="Choose workspace"
            value={duplicateWithWorkspaces}
            onChange={handleSelectChange}
          />
          <div className="d-flex">
            <StyledCheckbox
              id="makeCopy"
              name="makeCopy"
              className="my-2"
              label="Make a copy"
              checked={isMakeCopy}
              onChange={makeCopyToggle}
            />
            <QuestionMark
              className="DuplicateModal__question-mark ml-2 align-self-center"
              tooltip="Select this option to copy this item to multiple workspaces"
            />
          </div>
        </div>
      </WpModalBody>
      <WpModalFooter>
        <WpButtonGroup>
          <WpButton variant="tertiary" onClick={onToggle} disabled={isLoading}>
            Cancel
          </WpButton>
          <WpButton onClick={handleDuplicateClick} loading={isLoading}>
            Submit
          </WpButton>
        </WpButtonGroup>
      </WpModalFooter>
    </WpModal>
  );
};

export default DuplicateModal;
